import React, { useEffect, useMemo, useState } from 'react';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { Header } from './components/Header';
import { CategoryNav } from './components/CategoryNav';
import { CategoryGrid } from './components/CategoryGrid';
import { MenuSection } from './components/MenuSection';
import { BowlBuilder } from './components/BowlBuilder';
import { ProductCustomizer } from './components/ProductCustomizer';
import { Cart } from './components/Cart';
import { Checkout } from './components/Checkout';
import { CartBadge } from './components/CartBadge';
import { MENU_PRODUCTS, CATEGORIES } from './data/menuData';
import { Product, CartItemType } from './types/types';
import { useCart } from './context/CartContext';
import { AnimatePresence } from 'framer-motion';
import { RestaurantDashboard } from './admin/RestaurantDashboard';
import { QRGenerator } from './admin/QRGenerator';
import { TableOrderCheck } from './components/TableOrderCheck';
import { supabase } from './lib/supabase';
import { KitchenClosedBanner } from './components/KitchenClosedBanner';
import { ClosedHoursBanner } from './components/ClosedHoursBanner';
import { OrderTracking } from './components/OrderTracking';
import { OrderLookup } from './components/OrderLookup';
import { CONFIG } from './data/config';
type ViewState = 'menu' | 'bowl-builder' | 'product-customizer' | 'checkout' | 'restaurant-dashboard' | 'order-tracking' | 'qr-generator' | 'order-lookup';
function AppContent() {
  const [trackingToken, setTrackingToken] = useState<string | null>(null);
  const [view, setView] = useState<ViewState>(() => {
    // Check URL hash for admin access
    if (window.location.hash === '#admin' || window.location.hash === '#caja' || window.location.hash === '#cocina' || window.location.hash === '#pizzeria' || window.location.hash === '#bebidas') return 'restaurant-dashboard';
    if (window.location.hash === '#qr') return 'qr-generator';
    // Check URL path for tracking
    const path = window.location.pathname;
    if (path.startsWith('/track/')) {
      const token = path.split('/track/')[1];
      if (token) {
        return 'order-tracking';
      }
    }
    return 'menu';
  });
  // Set tracking token if we're in tracking view initially
  useEffect(() => {
    if (view === 'order-tracking') {
      const token = window.location.pathname.split('/track/')[1];
      if (token) setTrackingToken(token);
    }
  }, []);
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editingItem, setEditingItem] = useState<CartItemType | null>(null);
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [isOutsideHours, setIsOutsideHours] = useState(false);
  const [tableOrderCheck, setTableOrderCheck] = useState<{
    show: boolean;
    tableNumber: string;
    orderNumber: string;
    trackingToken: string;
  } | null>(null);
  const {
    addItem
  } = useCart();
  // Check for existing open orders on this table
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const table = params.get('table');
    if (!table || view !== 'menu') return;
    const checkTableOrders = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('orders').select('order_number, tracking_token, status').eq('table_number', table).in('status', ['new', 'accepted', 'preparing', 'ready']).order('created_at', {
          ascending: false
        }).limit(1);
        if (error) {
          console.error('Error checking table orders:', error);
          return;
        }
        if (data && data.length > 0) {
          setTableOrderCheck({
            show: true,
            tableNumber: table,
            orderNumber: data[0].order_number,
            trackingToken: data[0].tracking_token
          });
        }
      } catch (err) {
        console.error('Error checking table:', err);
      }
    };
    checkTableOrders();
  }, []);
  // Check operating hours
  useEffect(() => {
    const checkHours = () => {
      const now = new Date();
      const hour = now.getHours();
      const outside = hour < CONFIG.OPEN_HOUR || hour >= CONFIG.CLOSE_HOUR;
      setIsOutsideHours(outside);
    };
    checkHours();
    const interval = setInterval(checkHours, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  // Check kitchen status periodically
  useEffect(() => {
    if (view === 'restaurant-dashboard') return; // Dashboard has its own check
    const checkKitchenStatus = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('restaurant_settings').select('kitchen_paused').eq('id', 1).single();
        if (error) {
          console.error('Error fetching kitchen status:', error);
          return;
        }
        if (data) {
          setKitchenPaused(data.kitchen_paused);
          // If kitchen is paused and user is in checkout/builder, send them back to menu
          if (data.kitchen_paused && view !== 'menu') {
            setView('menu');
          }
          // Also check schedule — if outside hours, send back to menu
          const hour = new Date().getHours();
          const outside = hour < CONFIG.OPEN_HOUR || hour >= CONFIG.CLOSE_HOUR;
          if (outside && view !== 'menu') {
            setView('menu');
          }
        }
      } catch (err) {
        console.error('Error checking kitchen status:', err);
      }
    };
    checkKitchenStatus();
    const interval = setInterval(checkKitchenStatus, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [view]);
  const filteredProducts = useMemo(() => {
    if (activeCategory === 'Todos') return MENU_PRODUCTS;
    return MENU_PRODUCTS.filter(p => p.category === activeCategory);
  }, [activeCategory]);
  const groupedProducts = useMemo(() => {
    const groups: Record<string, Product[]> = {};
    filteredProducts.forEach(p => {
      if (!groups[p.category]) groups[p.category] = [];
      groups[p.category].push(p);
    });
    return groups;
  }, [filteredProducts]);
  const handleAddProduct = (product: Product) => {
    if (product.customizable) {
      setSelectedProduct(product);
      setEditingItem(null);
      setView('product-customizer');
    } else {
      addItem({
        id: `${product.id}_${Date.now()}`,
        productId: product.id,
        name: product.name,
        basePrice: product.price,
        quantity: 1,
        selections: {},
        subtotal: product.price,
        type: product.type || 'bebida'
      });
    }
  };
  const handleEditItem = (item: CartItemType) => {
    setEditingItem(item);
    if (item.type === 'bowl') {
      setView('bowl-builder');
    } else {
      const product = MENU_PRODUCTS.find(p => p.id === item.productId);
      if (product) {
        setSelectedProduct(product);
        setView('product-customizer');
      }
    }
  };
  return <div className="min-h-screen bg-carbon w-full flex justify-center" data-id="element-589">
      {/* Dashboard is full-width, outside the max-w-md container */}
      {view === 'restaurant-dashboard' && <RestaurantDashboard onBack={() => {
      window.location.hash = '';
      setView('menu');
    }} data-id="element-590" />}

      {/* QR Generator */}
      {view === 'qr-generator' && <QRGenerator onBack={() => {
      window.location.hash = '';
      setView('menu');
    }} data-id="element-591" />}

      {/* Tracking view is also full-width (handles its own max-w-md container) */}
      {view === 'order-tracking' && trackingToken && <OrderTracking token={trackingToken} data-id="element-592" />}

      {view !== 'restaurant-dashboard' && view !== 'order-tracking' && view !== 'qr-generator' && <div className="w-full max-w-md bg-carbon min-h-screen relative shadow-2xl overflow-x-hidden" data-id="element-593">
            {view === 'menu' && <>
                <Header onStartOrder={() => {
          setEditingItem(null);
          setView('bowl-builder');
        }} onLookupOrder={() => setView('order-lookup')} data-id="element-594" />

                <div className="px-4 pb-24" data-id="element-595">
                  <CategoryNav activeCategory={activeCategory} onSelectCategory={setActiveCategory} data-id="element-596" />

                  {activeCategory === 'Todos' ? <CategoryGrid onSelectCategory={setActiveCategory} data-id="element-597" /> : Object.entries(groupedProducts).map(([category, products]) => <MenuSection key={category} title={category} products={products} onAddProduct={handleAddProduct} data-id="element-598" />)}
                </div>

                {!kitchenPaused && !isOutsideHours && <>
                    <CartBadge data-id="element-599" />
                    <Cart onCheckout={() => setView('checkout')} onEditItem={handleEditItem} data-id="element-600" />
                  </>}

                {kitchenPaused && <KitchenClosedBanner data-id="element-601" />}
                {!kitchenPaused && isOutsideHours && <ClosedHoursBanner data-id="element-602" />}

                {/* Table order check modal */}
                {tableOrderCheck?.show && <TableOrderCheck tableNumber={tableOrderCheck.tableNumber} orderNumber={tableOrderCheck.orderNumber} onAddMore={() => setTableOrderCheck(null)} onViewOrder={() => {
          setTableOrderCheck(null);
          setTrackingToken(tableOrderCheck.trackingToken);
          setView('order-tracking');
          window.history.pushState({}, '', `/track/${tableOrderCheck.trackingToken}`);
        }} onClose={() => setTableOrderCheck(null)} data-id="element-603" />}
              </>}

            <AnimatePresence data-id="element-604">
              {view === 'bowl-builder' && <BowlBuilder initialItem={editingItem || undefined} onClose={() => setView('menu')} onSave={item => {
          addItem(item);
          setView('menu');
        }} data-id="element-605" />}

              {view === 'product-customizer' && selectedProduct && <ProductCustomizer product={selectedProduct} initialItem={editingItem || undefined} onClose={() => setView('menu')} onSave={item => {
          addItem(item);
          setView('menu');
        }} data-id="element-606" />}

              {view === 'checkout' && <Checkout onBack={() => setView('menu')} data-id="element-607" />}

              {view === 'order-lookup' && <OrderLookup onBack={() => setView('menu')} onTrackOrder={token => {
          setTrackingToken(token);
          setView('order-tracking');
          window.history.pushState({}, '', `/track/${token}`);
        }} data-id="element-608" />}
            </AnimatePresence>
          </div>}
    </div>;
}
export function App() {
  return <LanguageProvider data-id="element-609">
      <CartProvider data-id="element-610">
        <AppContent data-id="element-611" />
      </CartProvider>
    </LanguageProvider>;
}